// staging relative_path 사전 검증. backend `normalize_relative_path`(base/path.py)와 같은 규칙이다.
//
// Owns:
// - declare 에 보낼 경로의 정규화·거부 정책. 서버 422(배치 전체 거부)를 미리 막는 1차 방어선이다.
// - 서버보다 **엄격**한 규칙: 서버는 `.` 세그먼트를 허용하지만 FE 는 거부한다. GCS object key 에
//   `./` 리터럴이 그대로 들어가는 것을 막는다.
// - 유니코드 NFC 변환. macOS 의 파일 이름은 NFD 일 수 있다. 서버는 declare 본문에 NFC 가 아닌
//   문자열이 있으면 배치 전체를 422 로 거부한다. 서버는 경로의 글자를 바꾸지 않는다. 그래서 FE 가
//   경로를 NFC 로 바꿔서 보낸다.
//
// Does not own:
// - OS 잡파일 필터와 중복 판정. folderScan.ts 가 스캔 정책으로 소유한다.

// backend UploadFileDeclareItem.relative_path 의 max_length 와 같은 값.
export const MAX_RELATIVE_PATH_LENGTH = 1024;

export type RelativePathRejection =
  | "blank"
  | "backslash"
  | "traversal"
  | "dot_segment"
  | "too_long";

export type NormalizedRelativePath =
  | { ok: true; path: string }
  | { ok: false; reason: RelativePathRejection };

// 경로를 정규화한다. 서버 규칙: trim → `\` 거부 → `/` 로 분할하고 빈 세그먼트 제거 → `..` 거부.
// FE 추가 규칙: NFC 변환, `.` 세그먼트 거부, 정규화 후 길이 상한. 길이와 중복은 NFC 값으로 판정한다.
export function normalizeRelativePath(raw: string): NormalizedRelativePath {
  const trimmed = raw.normalize("NFC").trim();
  if (trimmed === "") {
    return { ok: false, reason: "blank" };
  }
  if (trimmed.includes("\\")) {
    return { ok: false, reason: "backslash" };
  }
  const segments = trimmed.split("/").filter((segment) => segment !== "");
  if (segments.length === 0) {
    return { ok: false, reason: "blank" };
  }
  if (segments.includes("..")) {
    return { ok: false, reason: "traversal" };
  }
  if (segments.includes(".")) {
    return { ok: false, reason: "dot_segment" };
  }
  const path = segments.join("/");
  if (path.length > MAX_RELATIVE_PATH_LENGTH) {
    return { ok: false, reason: "too_long" };
  }
  return { ok: true, path };
}
